<template>
  <CCIncentiveDetail
    v-if="selectedIncentive"
    :incentive="selectedIncentive"
    @back="selectedIncentive = null"
  />
  <div v-else class="cc-incentive-tracker-container">
    <div class="cc-header-card">
      <div class="cc-header">
        <span class="cc-header-title">{{ t('consultantCorner.IncentiveTracker') }}</span>
        <span class="cc-header-subtitle">{{ t('consultantCorner.IncentiveTrackerSubtitle') }}</span>
      </div>
    </div>

    <div class="cc-content">
      <div class="cc-description">
        <span class="cc-mini-header">{{ t('consultantCorner.AllIncentives') }}</span>
        <p>{{ t('consultantCorner.IncentiveTrackerDescription') }}</p>
      </div>

      <div class="cc-filters-section">
        <div class="cc-filter-buttons">
          <button
            v-for="option in statusFilterOptions"
            :key="option.value"
            :class="['cc-filter-button', { 'active': selectedStatusFilter === option.value }]"
            @click="onStatusFilterChange(option.value)"
          >
            {{ option.text }}
          </button>
        </div>
      </div>

      <div class="cc-incentives-grid-container">
        <div v-if="loading" class="cc-loading">
          {{ t('consultantCorner.LoadingIncentives') }}
        </div>
        <div v-else-if="filteredIncentives.length === 0" class="cc-no-results">
          {{ t('consultantCorner.NoIncentivesFound') }}
        </div>
        <div v-else class="cc-incentives-grid">
          <div class="cc-grid-header">
            <div class="cc-grid-header-cell cc-col-type">{{ t('consultantCorner.IncentiveType') }}</div>
            <div class="cc-grid-header-cell cc-col-name">{{ t('consultantCorner.IncentiveName') }}</div>
            <div class="cc-grid-header-cell cc-col-progress">{{ t('consultantCorner.OnTrack') }}</div>
            <div class="cc-grid-header-cell cc-col-step">{{ t('consultantCorner.Step') }}</div>
            <div class="cc-grid-header-cell cc-col-countdown">{{ t('consultantCorner.Countdown') }}</div>
            <div class="cc-grid-header-cell cc-col-status">{{ t('consultantCorner.Status') }}</div>
            <div class="cc-grid-header-cell cc-col-start cc-sortable" @click="toggleSort('startDate')">
              {{ t('consultantCorner.StartDate') }}
              <span v-if="sortField === 'startDate'" class="cc-sort-indicator">
                <i :class="['cc-sort-arrow', sortDirection === 'asc' ? 'cc-sort-up' : 'cc-sort-down']"></i>
              </span>
            </div>
            <div class="cc-grid-header-cell cc-col-end cc-sortable" @click="toggleSort('endDate')">
              {{ t('consultantCorner.EndDate') }}
              <span v-if="sortField === 'endDate'" class="cc-sort-indicator">
                <i :class="['cc-sort-arrow', sortDirection === 'asc' ? 'cc-sort-up' : 'cc-sort-down']"></i>
              </span>
            </div>
          </div>

          <div class="cc-grid-body">
            <!-- Desktop/Tablet Grid View -->
            <div v-for="incentive in filteredIncentives" :key="incentive.program.programId" class="cc-grid-row cc-desktop-view">
              <div class="cc-grid-cell cc-col-name">
                  {{ incentive.incentiveType.localizedValue }}
              </div>
              <div class="cc-grid-cell cc-col-name">
                <a href="#" @click.prevent="viewIncentiveDetails(incentive)" class="cc-incentive-link">
                  {{ incentive.program.programName }}
                </a>
              </div>
              <div class="cc-grid-cell cc-col-progress">
                <div class="cc-progress-container">
                  <ProgressBar
                    :value="calculateProgress(incentive)"
                    :max="100"
                    :class="['cc-progress-bar', {
                      'cc-progress-active': getIncentiveStatus(incentive) === t('consultantCorner.Active'),
                      'cc-progress-closed': getIncentiveStatus(incentive) === t('consultantCorner.Closed'),
                      'cc-progress-qualified': getIncentiveStatus(incentive) === t('consultantCorner.Qualified'),
                      'cc-progress-other': getIncentiveStatus(incentive) === t('consultantCorner.Other')
                    }]"
                  />
                  <span class="cc-progress-text">{{ Math.round(calculateProgress(incentive)) }}%</span>
                </div>
              </div>
              <div class="cc-grid-cell cc-col-step">
                {{ incentive.level?.levelName || '' }}
              </div>
              <div class="cc-grid-cell cc-col-countdown">
                <span class="cc-countdown">
                  {{ formatCountdown(incentive.daysLeft) }}
                </span>
              </div>
              <div class="cc-grid-cell cc-col-status">
                <span :class="['cc-status-pill', {
                  'cc-status-active': getIncentiveStatus(incentive) === t('consultantCorner.Active'),
                  'cc-status-closed': getIncentiveStatus(incentive) === t('consultantCorner.Closed'),
                  'cc-status-qualified': getIncentiveStatus(incentive) === t('consultantCorner.Qualified'),
                  'cc-status-other': getIncentiveStatus(incentive) === t('consultantCorner.Other')
                }]">
                  <span class="cc-status-dot"></span>
                  {{ getIncentiveStatus(incentive) }}
                </span>
              </div>
              <div class="cc-grid-cell cc-col-start">
                {{ formatDate(incentive.startDate) }}
              </div>
              <div class="cc-grid-cell cc-col-end">
                {{ formatDate(incentive.endDate) }}
              </div>
            </div>

            <!-- Mobile Card View -->
            <div v-for="incentive in filteredIncentives" :key="'mobile-'+incentive.program.programId" class="cc-mobile-card">
              <div class="cc-mobile-card-header">
                <span :class="['cc-status-pill', {
                  'cc-status-active': getIncentiveStatus(incentive) === t('consultantCorner.Active'),
                  'cc-status-closed': getIncentiveStatus(incentive) === t('consultantCorner.Closed'),
                  'cc-status-qualified': getIncentiveStatus(incentive) === t('consultantCorner.Qualified'),
                  'cc-status-other': getIncentiveStatus(incentive) === t('consultantCorner.Other')
                }]">
                  <span class="cc-status-dot"></span>
                  {{ getIncentiveStatus(incentive) }}
                </span>
              </div>

              <h3 class="cc-mobile-card-title">
                <a href="#" @click.prevent="viewIncentiveDetails(incentive)" class="cc-incentive-link">
                  {{ incentive.program.programName }}
                </a>
              </h3>

              <div class="cc-mobile-card-type">{{ incentive.incentiveType.localizedValue }}</div>

              <div class="cc-mobile-card-progress">
                <div class="cc-mobile-progress-label">{{ t('consultantCorner.OnTrack') }}</div>
                <div class="cc-progress-container">
                  <ProgressBar
                    :value="calculateProgress(incentive)"
                    :max="100"
                    :class="['cc-progress-bar', {
                      'cc-progress-active': getIncentiveStatus(incentive) === t('consultantCorner.Active'),
                      'cc-progress-closed': getIncentiveStatus(incentive) === t('consultantCorner.Closed'),
                      'cc-progress-qualified': getIncentiveStatus(incentive) === t('consultantCorner.Qualified'),
                      'cc-progress-other': getIncentiveStatus(incentive) === t('consultantCorner.Other')
                    }]"
                  />
                  <span class="cc-progress-text">{{ Math.round(calculateProgress(incentive)) }}%</span>
                </div>
              </div>

              <div class="cc-mobile-card-details">
                <div class="cc-mobile-detail-item">
                  <div class="cc-mobile-detail-label">{{ t('consultantCorner.Step') }}</div>
                  <div class="cc-mobile-detail-value">{{ incentive.level?.levelName || '-' }}</div>
                </div>

                <div class="cc-mobile-detail-item">
                  <div class="cc-mobile-detail-label">{{ t('consultantCorner.Countdown') }}</div>
                  <div class="cc-mobile-detail-value">{{ formatCountdown(incentive.daysLeft) }}</div>
                </div>

                <div class="cc-mobile-detail-item">
                  <div class="cc-mobile-detail-label">{{ t('consultantCorner.StartDate') }}</div>
                  <div class="cc-mobile-detail-value">{{ formatDate(incentive.startDate) }}</div>
                </div>

                <div class="cc-mobile-detail-item">
                  <div class="cc-mobile-detail-label">{{ t('consultantCorner.EndDate') }}</div>
                  <div class="cc-mobile-detail-value">{{ formatDate(incentive.endDate) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { ProgressBar } from '@progress/kendo-vue-progressbars'
import { PwsActiveBeeIncentiveGetList } from '../../api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'
import { handleError } from '../../services/ErrorService'
import type { IPwsBeeIncentiveSummary } from '../../types/ConsultantCornerTypes'
import CCIncentiveDetail from './CCIncentiveDetail.vue'

const { t } = useI18n()
const logger = useLogger()

const incentives = ref<IPwsBeeIncentiveSummary[]>([])
const selectedStatusFilter = ref('all')
const sortField = ref('')
const sortDirection = ref('asc')
const loading = ref(false)
const selectedIncentive = ref<IPwsBeeIncentiveSummary | null>(null)
const showIncentiveDetail = ref(false)

const statusFilterOptions = [
  { text: t('consultantCorner.Active'), value: 'active' },
  { text: t('consultantCorner.Closed'), value: 'closed' },
  { text: t('consultantCorner.Qualified'), value: 'qualified' },
  { text: t('consultantCorner.ViewAll'), value: 'all' }
]

const sortIncentives = (incentives: IPwsBeeIncentiveSummary[]) => {
  if (!sortField.value) return incentives;

  return [...incentives].sort((a, b) => {
    let valueA, valueB;

    if (sortField.value === 'startDate') {
      valueA = new Date(a.startDate).getTime();
      valueB = new Date(b.startDate).getTime();
    } else if (sortField.value === 'endDate') {
      valueA = new Date(a.endDate).getTime();
      valueB = new Date(b.endDate).getTime();
    } else {
      return 0;
    }

    return sortDirection.value === 'asc' ? valueA - valueB : valueB - valueA;
  });
};

const filteredIncentives = computed(() => {
  let filtered;

  if (selectedStatusFilter.value === 'all') {
    filtered = incentives.value;
  } else {
    const today = new Date();
    filtered = incentives.value.filter(incentive => {
      const startDate = new Date(incentive.startDate);
      const endDate = new Date(incentive.endDate);
      const closeDate = new Date(incentive.closeDate);

      switch (selectedStatusFilter.value) {
        case 'active':
          return today >= startDate && today <= endDate;
        case 'closed':
          return today > closeDate;
        case 'qualified':
          // Check if all award criteria have pointsEarned > pointsRequired
          if (!incentive.level?.compAwardStatus?.[0]?.awardStatus) return false;

          const awardStatus = incentive.level.compAwardStatus[0].awardStatus;
          return awardStatus.every(item => item.pointsEarned > item.pointsRequired);
        default:
          return true;
      }
    });
  }

  return sortIncentives(filtered);
})

const calculateProgress = (incentive: IPwsBeeIncentiveSummary): number => {
  if (!incentive.buckets || incentive.buckets.length === 0 || !incentive.level?.compAwardStatus?.[0]?.awardStatus) {
    logger.info(`No buckets or award status found for incentive: ${incentive.program.programName}`)
    return 0
  }

  logger.info(`Calculating progress for incentive: ${incentive.program.programName}`)

  const awardStatus = incentive.level.compAwardStatus[0].awardStatus
  let totalCurrent = 0
  let totalTarget = 0

  incentive.buckets.forEach(bucket => {
    const award = awardStatus.find(a => a.criteriaNumber === bucket.awardCriteria)
    const current = award?.pointsEarned || 0
    const target = award?.pointsRequired || 0

    logger.info(`Bucket: ${bucket.bucketName || 'Unnamed'} - Current: ${current}, Target: ${target}`)

    totalCurrent += current
    totalTarget += target
  })

  if (totalTarget === 0) {
    logger.info('Total target value is 0, returning 0% progress')
    return 0
  }

  const progress = Math.min((totalCurrent / totalTarget) * 100, 100)
  logger.info(`Total progress: ${progress.toFixed(2)}% (Current: ${totalCurrent}, Target: ${totalTarget})`)
  return progress
}

const formatCountdown = (daysLeft?: number): string => {
  if (daysLeft === undefined || daysLeft === null) {
    return ''
  }

  if (daysLeft <= 0) {
    return t('consultantCorner.Closed')
  }

  return `${daysLeft} ${t('consultantCorner.DaysLeft')}`
}

const getCountdownClass = (daysLeft?: number): string => {
  if (!daysLeft) return 'cc-countdown-expired'

  if (daysLeft <= 0) {
    return 'cc-countdown-expired'
  } else if (daysLeft <= 7) {
    return 'cc-countdown-urgent'
  } else if (daysLeft <= 30) {
    return 'cc-countdown-warning'
  } else {
    return 'cc-countdown-normal'
  }
}

const getIncentiveStatus = (incentive: IPwsBeeIncentiveSummary): string => {
  const today = new Date()
  const startDate = new Date(incentive.startDate)
  const endDate = new Date(incentive.endDate)
  const closeDate = new Date(incentive.closeDate)

  if (today >= startDate && today <= endDate) {
    return t('consultantCorner.Active')
  } else if (today > closeDate) {
    return t('consultantCorner.Closed')
  } else if (incentive.level?.compAwardStatus?.[0]?.awardStatus?.every(item => item.pointsEarned > item.pointsRequired)) {
    return t('consultantCorner.Qualified')
  } else {
    return t('consultantCorner.Other')
  }
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const viewIncentiveDetails = (incentive: IPwsBeeIncentiveSummary) => {
  selectedIncentive.value = incentive
  showIncentiveDetail.value = true
}

const toggleSort = (field: string) => {
  if (sortField.value === field) {
    // Toggle direction if already sorting by this field
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new sort field and default to ascending
    sortField.value = field
    sortDirection.value = 'asc'
  }
}

const onStatusFilterChange = (value: string) => {
  selectedStatusFilter.value = value
  applyFilters()
}

const applyFilters = () => {
  logger.info('Applying filters with status:', selectedStatusFilter.value)
}

const loadIncentives = async () => {
  try {
    loading.value = true
    const response = await PwsActiveBeeIncentiveGetList()
    incentives.value = response.items || []
  } catch (error) {
    logger.error('Error loading incentives:', error)
    await handleError(error, t)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadIncentives()
})
</script>

<style lang="scss" scoped>
.cc-incentive-tracker-container {
  width: 100%;
  max-width: 100%;
  padding: $card-padding;
}

.cc-incentive-tracker-container .k-progress-status{
  display: none;
}

.cc-header-card {
  background-color: white;

  margin-bottom: 20px;
  overflow: hidden;
}

.cc-header {

  color: black;
  padding: $spacing-2-5 $spacing-4;
  text-align: left;
}

.cc-header-title {
  @include typography-h4;
  @include font-weight-medium;
  letter-spacing: 0.5px;
  display: block;
}

.cc-content {

    margin: 0 auto;
    background-color: var(--vt-c-white);
    border-radius: $radius-xl $radius-xl $radius-md $radius-md;

    border: 1px solid rgba(#7e7e7e, 0.2);
    text-align: left;
}

.cc-description {
  margin-bottom: 20px;
  text-align: left;
  color: #666;
  padding-left: 20px;
    padding-top: 10px;
    border-bottom: 1px solid rgba(#7e7e7e, 0.2);
}

.cc-filters-section {
  margin-bottom: 20px;
  padding-left:20px;

  .cc-filter-buttons {
    display: flex;
    gap: $spacing-1;

    .cc-filter-button {
      padding: $spacing-2 $spacing-4;
      border: 1px solid #e0e0e0;
      @include typography-small;
      background: white;
      color: #666;
      cursor: pointer;
      border-radius: $radius-xl;
      transition: all 0.2s;

      &:hover {
        background-color: var(--vt-c-white-soft);
      }

      &.active {
        background-color: var(--vt-c-white-soft);
        border-color: #cccccc;
        color: #333;
      }
    }
  }
}

.cc-filter-row {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
  align-items: center;
}

@media (min-width: 768px) {
  .cc-filter-row {
    flex-direction: row;
    justify-content: space-between;
  }
}

.cc-filter-container {
  display: flex;
  align-items: center;
  gap: $spacing-2-5;
}

.cc-filter-label {
  @include font-weight-medium;
  color: #333;
  white-space: nowrap;
}

.cc-status-dropdown {
  min-width: 150px;
}

.cc-apply-button {
  background-color: #3498db;
  color: white;
  @include typography-small;
  border: none;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-sm;
  transition: background-color 0.2s ease;
}

.cc-apply-button:hover {
  background-color: #2980b9;
}

.cc-incentives-grid-container {
  min-height: 300px;
}

.cc-loading, .cc-no-results {
  text-align: center;
  padding: $spacing-10 $spacing-5;
  color: #666;
  font-style: italic;
}

.cc-incentives-grid {
  border: 1px solid #e9ecef;
  border-radius: $radius-sm;
  overflow: hidden;
}

.cc-grid-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1.5fr 1fr 1fr 1fr 1fr 1fr;
  background-color: var(--vt-c-white-soft);
  border-bottom: 2px solid #e9ecef;
}

.cc-grid-header-cell {
  @include typography-caption;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-1;
  text-transform: uppercase;
  color: #333;
  border-right: 1px solid #e9ecef;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cc-grid-header-cell:last-child {
  border-right: none;
}

.cc-sortable {
  cursor: pointer;
  position: relative;
  user-select: none;
}

.cc-sortable:hover {
  background-color: #f0f4f7;
}

.cc-sort-indicator {
  display: inline-block;
  margin-left: 4px;
}

.cc-sort-arrow {
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  position: relative;
}

.cc-sort-up {
  border-bottom: 5px solid #333;
  top: -2px;
}

.cc-sort-down {
  border-top: 5px solid #333;
  top: 2px;
}

.cc-grid-body {
  max-height: 500px;
  overflow-y: auto;
}

.cc-grid-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1.5fr 1fr 1fr 1fr 1fr 1fr;
  border-bottom: 1px solid var(--vt-c-white-soft);
  transition: background-color 0.2s ease;
}

.cc-grid-row:hover {
  background-color: var(--vt-c-white-soft);
}

.cc-grid-cell {
  @include typography-small;
  padding: $spacing-3 $spacing-1;
  border-right: 1px solid var(--vt-c-white-soft);
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cc-grid-cell:last-child {
  border-right: none;
}

/* Mobile Card Styles */
.cc-mobile-card {
  display: none; /* Hidden by default, shown in mobile view */
  background-color: white;
  border-radius: $radius-md;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  margin-bottom: 16px;
  padding: $spacing-4;
  position: relative;
}

.cc-mobile-card-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.cc-mobile-card-title {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 8px 0;
}

.cc-mobile-card-type {
  @include typography-small;
  color: #666;
  margin-bottom: 16px;
}

.cc-mobile-card-progress {
  margin-bottom: 20px;
}

.cc-mobile-progress-label {
  @include typography-small;
  @include font-weight-medium;
  margin-bottom: 8px;
}

.cc-mobile-card-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing-3;
  margin-top: 16px;
}

.cc-mobile-detail-item {
  margin-bottom: 8px;
}

.cc-mobile-detail-label {
  @include typography-caption;
  color: #666;
  margin-bottom: 4px;
}

.cc-mobile-detail-value {
  @include typography-small;
  @include font-weight-medium;
}

.cc-incentive-link {
  @include font-weight-medium;
  color: #3498db;
  text-decoration: none;
}

.cc-incentive-link:hover {
  text-decoration: underline;
  color: #2980b9;
}

.cc-progress-container {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  width: 100%;
}

.cc-progress-bar {
  flex: 1;
  margin-right: 10px;
  height: 8px;

  :deep(.k-progressbar-text) {
    display: none;
  }
}

.cc-progress-active  {
  :deep(.k-progressbar-value.k-selected) {
    background-color: var(--vt-c-teal) !important;
  }
}

.cc-progress-closed {
  :deep(.k-progressbar-fill) {
    background-color: #e74c3c !important;
  }
}

.cc-progress-qualified {
  :deep(.k-progressbar-fill) {
    background-color: #27ae60 !important;
  }
}

.cc-progress-other {
  :deep(.k-progressbar-fill) {
    background-color: #95a5a6 !important;
  }
}

.cc-progress-text {
  @include typography-caption;
  @include font-weight-medium;
  color: #666;
  white-space: nowrap;
}

.cc-countdown-normal {
  @include font-weight-medium;
  color: #27ae60;
}

.cc-countdown-warning {
  @include font-weight-medium;
  color: #f39c12;
}

.cc-countdown-urgent {
  @include font-weight-semibold;
  color: #e74c3c;
}

.cc-countdown-expired {
  color: #95a5a6;
  font-style: italic;
}

.cc-status-pill {
  display: inline-flex;
  align-items: center;
  @include typography-caption;
  @include font-weight-medium;
  padding: $spacing-1 $spacing-3;
  border-radius: $radius-xl;
  text-align: center;
  white-space: nowrap;
}

.cc-status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.cc-status-active {
  background-color: var(--vt-c-teal)44;
  color: var(--vt-c-teal);

  .cc-status-dot {
    background-color: var(--vt-c-teal);
  }
}

.cc-status-closed {
  background-color: rgba(#e74c3c, 0.3);
  color: #e74c3c;

  .cc-status-dot {
    background-color: #e74c3c;
  }
}

.cc-status-qualified {
  background-color: rgba(#27ae60, 0.3);
  color: #27ae60;

  .cc-status-dot {
    background-color: #27ae60;
  }
}

.cc-status-other {
  background-color: #95a5a6;
  color: white;

  .cc-status-dot {
    background-color: white;
  }
}

@media (max-width: 1024px) {
  .cc-grid-header,
  .cc-grid-row {
    grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr;
  }

  .cc-col-type,
  .cc-col-start {
    display: none;
  }
}

@media (max-width: 768px) {
  .cc-content {
    padding: $spacing-4;
  }

  .cc-filter-buttons {overflow:scroll;}

  /* Hide desktop view */
  .cc-grid-header,
  .cc-desktop-view {
    display: none;
  }

  /* Show mobile cards */
  .cc-mobile-card {
    display: block;
  }

  .cc-grid-header-cell,
  .cc-grid-cell {
    @include typography-caption;
    padding: $spacing-2 $spacing-2;
  }

  .cc-progress-container {
    flex-direction: column;
    gap: $spacing-1;
  }

  .cc-progress-text {
    @include typography-caption;
  }
}

@media (max-width: 480px) {
  .cc-mobile-card-details {
    grid-template-columns: 1fr;
  }

  .cc-content {
    padding: $spacing-2-5;
  }

  .cc-mobile-card {
    padding: $spacing-3;
    margin-bottom: 12px;
  }
}

.cc-mini-header {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 10px;
}
.aoe-incentive-tracker{max-width:1200px;}
</style>
